import { Button, Input, Modal, Select, Space, DatePicker, theme, Tabs, Upload, Form, Spin, message, Pagination, Checkbox } from 'antd';
import { SearchOutlined, SettingOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { orderColumns } from './constants';
import { getPacsStatusLabel, FieldLabels, getPatientIdLabel } from '../../constants/fieldLabels';
import "./worklist.css";
import axiosInstance from '../../axios';
import { getUserDetails, getDefaultCenterFilter, hisStatusOptions, makePostCall, makeGetCall, hasReportingPermission } from '../../utils/helper';
import { maxTagTooltip } from '../../utils/selectHelpers';
import ColumnConfigurator from '../../components/PacsAgGrid/ColumnConfigurator';
import { ColumnConfig, ColumnHeaderActions, PinnedColumnsConfig } from '../../components/PacsAgGrid/types';
import { useSites } from '../../hooks/useSites';
import { useAllowedModalities } from '../../hooks/useAllowedModalities';
import { useBodyParts } from '../../hooks/useBodyParts';
import { useModalityLocations } from '../../hooks/useModalityLocations';
import { useRegistrationTypes } from '../../hooks/useRegistrationTypes';
import dayjs, { type Dayjs } from 'dayjs';
import FyzksInput from '../../components/FyzksInput';
import { debounce, throttle } from 'lodash';
import ViewNotes from '../ManageNodes/ViewNotes';
import OrderInfoPopup from '../OrderInfo';
import PdfViewer from '../../components/PdfViewer';
import ShareStudyModal from '../../components/ShareStudyModal';
import SeriesSelectionModal from '../../components/SeriesSelectionModal';
import DownloadStudyModal from '../../components/DownloadStudyModal';
import ExportStudyModal from '../../components/ExportStudyModal';
import SplitStudyModal from '../../components/SplitStudyModal';
import TextArea from 'antd/es/input/TextArea';
import TabPane from 'antd/es/tabs/TabPane';
import { useForm } from 'antd/es/form/Form';
// Import shared action handlers
import * as actionHandlers from '../../utils/tableActionHandlers';
import { openSeriesViewer, openReportingWindow } from '../../utils/MonitorManager';
import { useWebSocketContext } from '../../context/WebSocketContext';
import { getAutoRefreshEnabled, subscribeAutoRefresh } from '../../utils/autoRefreshPubSub';
import { SavedSearches } from '../orders/constants';
import PacsAgGrid from '../../components/PacsAgGrid';
import { buildLeadingActionColumns, REFERENCE_LAYOUT_MARKER } from '../../utils/sharedTableColumns';
import { mergeShortcuts, shortcutToFilterAndRange, SHORTCUTS_UPDATED_EVENT } from '../../utils/shortcuts';
import QuickDatePresets from '../../components/QuickDatePresets';
import {sortedModalityOptionsNew} from '../../utils/helper';
import {CENTER_RADIOLOGY_URL} from '../../constants/APP_CONSTANTS';

const { RangePicker } = DatePicker;

const MyWorklist = ({ StyledTabsComponent }) => {
  const { token } = theme.useToken();
  const [orders, setOrders] = useState({ data: [], loading: true, total: 0 });
  const [filters, setFilters] = useState<Record<string, any>>(() => ({ center: getDefaultCenterFilter() }));
  const [modalityAllSelected, setModalityAllSelected] = useState(true);
  const [savedFilters, setSavedFilters] = useState([]);
  const [quickFilters, setQuickFilters] = useState([]);
  const userDetails = getUserDetails();
  const [userList, setUserList] = useState([]);
  const [isUserIdle, setIsUserIdle] = useState(false);
  const lastActivityRef = useRef(Date.now());

  // Use dynamic configuration hooks
  const { centers, loading: sitesLoading } = useSites();
  const { modalityOptions, loading: modalitiesLoading } = useAllowedModalities();
  const { bodyPartOptions, loading: bodyPartsLoading } = useBodyParts();
  // Location options are scoped to the selected Site/Group: each entry in the
  // modality_locations master carries an optional centre, and the ones without
  // a centre stay visible everywhere. Mirrors the PACS page.
  const { locationOptions, loading: locationsLoading, error: locationsError } = useModalityLocations(filters['center']);
  const { regTypeOptions, loading: regTypesLoading } = useRegistrationTypes();

  // Drop any selected location that the narrowed site selection no longer
  // offers, so the query can't filter on an option that isn't in the dropdown.
  useEffect(() => {
    // Never prune off a failed fetch — an empty option list there means "we
    // don't know", not "nothing is valid".
    if (locationsLoading || locationsError) return;
    setFilters(prev => {
      const selected = prev['modality_location'];
      if (!Array.isArray(selected) || selected.length === 0) return prev;
      const valid = new Set(locationOptions.map(o => o.value));
      const pruned = selected.filter(v => valid.has(v));
      if (pruned.length === selected.length) return prev;
      return { ...prev, modality_location: pruned.length > 0 ? pruned : undefined };
    });
  }, [locationOptions, locationsLoading, locationsError]);
   const sortedModalityOptions = sortedModalityOptionsNew(modalityOptions);
  // Set the default value to [yesterday at 00:00, today at 23:59]
  const today = dayjs();
  const yesterday = dayjs().subtract(1, 'days');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>([
    yesterday.hour(0).minute(0),
    today.hour(23).minute(59)
  ]);
  const [refreshDisabled, setRefreshDisabled] = useState(false);

  const [viewNotesModal, setViewNotesModal] = useState({ visible: false, details: null });
  const [selectedNote, setSelectedNote] = useState({});
  const [noteEditActive, setNoteEditActive] = useState(false);
  const [saveNoteHandler, setSaveNoteHandler] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(getAutoRefreshEnabled());
  const [selectedRowKeys, setSelectedRowKeys] = useState<(string | number)[]>([]);
  const selectedStudyRecords = useMemo(() => {
    if (!selectedRowKeys?.length || !orders?.data?.length) return [];
    const keySet = new Set(selectedRowKeys.map(k => String(k)));
    return (orders.data as any[]).filter((r: any) =>
      keySet.has(String(r?.pacs_order?.pacs_ord_id))
    );
  }, [selectedRowKeys, orders?.data]);

  const radHodOptions = useMemo(() => {
    return (userList || [])
      .filter(user => ['radiologist', 'hod'].includes(user.user_type))
      .map(user => ({
        label: `${user.user_fullname} (${user.username})`,
        value: user.username,
      }));
  }, [userList]);

  // Column configuration state
  const [columnOrder, setColumnOrder] = useState<any[]>([]);
  const [columnConfiguratorVisible, setColumnConfiguratorVisible] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>([]);
  const [pinnedColumns, setPinnedColumns] = useState<PinnedColumnsConfig>({ left: [], right: [] });

  // WebSocket context for real-time updates
  const wsContext = useWebSocketContext();

  useEffect(() => {
    const unsubscribe = subscribeAutoRefresh((enabled) => {
      setAutoRefreshEnabled(enabled);
    });

    return () => unsubscribe();
  }, []);

  // Handle priority filter changes
  const handlePriorityChange = (priority) => {
    if (priority === 'all') {
      // Remove po_priority from filters when "All priorities" is selected
      const temp_filters = { ...filters };
      delete temp_filters['po_priority'];
      setFilters(temp_filters);
      setSelectedPriority(null);
    } else {
      // Set specific priority filter
      handleFilterChange('po_priority', priority);
      setSelectedPriority(priority);
    }
  };

  // Additional state for new functionality (matching PACS page)
  const [orderInfoModal, setOrderInfoModal] = useState({});
  const [viewerModal, setViewerModal] = useState({ visible: false, data: {} });
  const [printLoading, setPrintLoading] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [selectedStudyForShare, setSelectedStudyForShare] = useState(null);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [selectedStudyForDownload, setSelectedStudyForDownload] = useState(null);
  const [splitStudyModalVisible, setSplitStudyModalVisible] = useState(false);
  const [selectedStudyForSplit, setSelectedStudyForSplit] = useState(null);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [selectedStudyForExport, setSelectedStudyForExport] = useState(null);
  const [receiverForm] = useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  // Series selection modal state. The series fetch + table live in the shared
  // <SeriesSelectionModal>; this only tracks which study is open.
  const [seriesSelectionModal, setSeriesSelectionModal] = useState({
    open: false,
    studyUID: null,
    record: null,
  });

  useEffect(() => {
    // getOrdersList();
    getUsersList();
    getSavedFilters();
    getQuickFilters();
    loadColumnPreferences();
  }, []);

  // Refresh chips when the user edits shortcuts from the profile dialog.
  useEffect(() => {
    const handler = () => getQuickFilters();
    window.addEventListener(SHORTCUTS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(SHORTCUTS_UPDATED_EVENT, handler);
  }, []);

  useEffect(() => {
    console.log("Filters changed", filters);
     // Reset to page 1 when filters change
    if (Object.keys(filters).length > 0 || dateRange) {
      setPagination({ current: 1, pageSize: pagination.pageSize });
    }
    debouncedFilter(filters);
  }, [filters]);

  useEffect(() => {
    console.log("date changed", filters);
    // if (Object.keys(filters).length) {
    debouncedFilter(filters);
    // }
  }, [dateRange]);

  useEffect(() => {
      const unsubscribe = subscribeAutoRefresh((enabled) => {
        setAutoRefreshEnabled(enabled);
      });

    return () => unsubscribe();

  }, []);

  // Auto-refresh effect
  useEffect(() => {
    let intervalId;

    if (autoRefreshEnabled && !isUserIdle) {
      intervalId = setInterval(() => {
        // Only refresh if not currently loading and filters are applied
        if (!orders.loading) {
          console.log('Auto-refreshing worklist...');
          // Refresh current page only - maintains user position
          filterResults(filters, pagination.current, pagination.pageSize);
        }
      }, 5 * 60 * 1000); // 5 minutes
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefreshEnabled, isUserIdle, orders.loading, filters, dateRange, pagination.current, pagination.pageSize]);


  // Subscribe to WebSocket messages for real-time study updates
  useEffect(() => {
    const unsubscribe = wsContext.subscribe((data) => {
      try {
        // Handle study update notifications using state values directly
        if (data.type === 'study_update' && autoRefreshEnabled && !isUserIdle) {
          console.log('[PACS] Received WebSocket message:', data);
          // Only refresh if not currently loading
          if (!orders.loading) {
            console.log('Auto-refreshing studies list...');
            filterResults(filters, pagination.current, pagination.pageSize);
          }
        }
      } catch (e) {
        console.error('[PACS] Error in WS handler:', e);
      }
    });
      return () => unsubscribe();
  }, [wsContext, autoRefreshEnabled, isUserIdle, orders.loading, filters, pagination]);
  // Idle detection effect
  useEffect(() => {
    const IDLE_TIME = 5 * 60 * 1000; // 5 minutes of inactivity

    const resetIdleTimer = () => {
      lastActivityRef.current = Date.now();
      if (isUserIdle) {
        setIsUserIdle(false);
      }
    };

    // Throttle the idle timer reset to prevent excessive function calls
    const throttledResetIdleTimer = throttle(resetIdleTimer, 1000, {
      leading: true,
      trailing: false
    });

    const checkIdleStatus = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;

      if (timeSinceActivity >= IDLE_TIME && !isUserIdle) {
        setIsUserIdle(true);
        console.log('User is idle, pausing auto-refresh');
      } else if (timeSinceActivity < IDLE_TIME && isUserIdle) {
        setIsUserIdle(false);
        console.log('User is active, resuming auto-refresh');
      }
    };

    // Events to track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, throttledResetIdleTimer, true);
    });

    // Check idle status every 30 seconds
    const idleCheckInterval = setInterval(checkIdleStatus, 30 * 1000);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledResetIdleTimer, true);
      });
      clearInterval(idleCheckInterval);
    };
  }, [isUserIdle]);

  // const debouncedFilter = debounce(() => { filterResults() }, 300);

  const debouncedFilter = useCallback(
    debounce((tempFilters) => {
      console.log('Debounced value:');
      // When filters change via debounce, use page 1 (already set in useEffect)
      filterResults(tempFilters, 1, pagination.pageSize);
    }, 500),
    [dateRange, pagination.pageSize]
  );

  const getUsersList = () => {
    makePostCall('/user-list', {}).then(res => {
      setUserList(res.data?.data || []);
    })
      .catch(e => {
        console.log(e);
        setUserList([]);
      })
  }

  const getQuickFilters = () => {
    makePostCall('/get-my-shortcuts', { user_id: getUserDetails()?.username })
      .then(res => {
        setQuickFilters(mergeShortcuts(res.data?.data));
      })
      .catch(e => {
        console.log(e);
        setQuickFilters([]);
      });
  }

  const getSavedFilters = () => {
    makePostCall('/get-saved-filters', { user_id: getUserDetails()?.username })
      .then(res => {
        setSavedFilters(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setSavedFilters([]);
      });
  }


  const getOrdersList = async () => {
    makePostCall('/my-worklist', {
      role: userDetails?.user_type,
      user_id: getUserDetails()?.username,
    })
      .then(res => {
        setOrders({ data: res.data?.data || [], loading: false })
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false })
      });
  }

  // Removed modal functionality - everything opens in new tab now

  const filterResults = (tempFilters, page = 1, pageSize = 20) => {
    setOrders({ ...orders, loading: true, data: [] });
    const payload = {
      ...tempFilters,
      role: userDetails?.user_type,
      user_id: getUserDetails()?.username,
      page: page - 1, // Backend expects 0-indexed page
      limit: pageSize
    };

    if (dateRange?.[0] && dateRange?.[1]) {
      payload['from_date'] = dayjs(dateRange[0]).format('YYYYMMDDHHmmss');
      payload['to_date'] = dayjs(dateRange[1]).format('YYYYMMDDHHmmss');
      payload['po_study_date'] = dateRange;
    }

    makePostCall('/my-worklist', payload)
      .then(res => {
        setOrders({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0
        })
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false, total: 0 })
      });
  }

  const handleFilterChange = (key, value) => {
    const temp_filters = { ...filters };
    temp_filters[key] = value;
    setFilters(temp_filters);
  }

  const refreshScanStatus = () => {
    setRefreshDisabled(true); // Disable the button
    setTimeout(() => {
      setRefreshDisabled(false); // Re-enable the button after 5 seconds
    }, 20000);
    axiosInstance.get('/update-status')
      .then(res => {
        // console.log("res", res);
        // Refresh with current pagination
        filterResults(filters, pagination.current, pagination.pageSize);
      })
      .catch(e => {
        console.log(e);
      })
  }

  const debouncedRefresh = useCallback(
    debounce(() => {
      console.log('Debounced value:');
      refreshScanStatus();
    }, 200),
    []
  );

  const statusOptions = userDetails?.user_type === 'doc' ? [
    { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
  ] :
    [
      { label: 'NEW', value: 'PENDING' },
      { label: getPacsStatusLabel('SCANNED'), value: 'SCANNED' },
      { label: 'DRAFTED', value: 'DRAFTED' },
      { label: 'REVIEWED', value: 'REVIEWED' },
      { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
      { label: 'CANCELLED', value: 'CANCELLED' },
    ]

  // All options now come from dynamic hooks:
  // siteOptions, modalityOptions, bodyPartOptions

  const reportedByOptions = useMemo(() => {
    return userList?.map((user) => ({ label: user.user_fullname, value: user.username }))
  }, [userList]);

  const viewNotes = (rec) => {
    console.log("view notes", rec);
    const firstNote = rec.note_id || {};
    setSelectedNote(firstNote)
    setNoteEditActive(false);
    setViewNotesModal({ visible: true, details: rec })
  }

  const handleNoteSelection = useCallback((note, _record) => {
    setSelectedNote(prevSelectedNote => {
      const previousNoteId = (prevSelectedNote as any)?.rn_id || null;
      const nextNoteId = note?.rn_id || null;
      const previousNoteText = (prevSelectedNote as any)?.rn_notes || '';
      const nextNoteText = note?.rn_notes || '';

      if (previousNoteId === nextNoteId && previousNoteText === nextNoteText) {
        return prevSelectedNote;
      }

      return note || {};
    });
  }, [])

  const clearFilters = () => {
    const defaultFilters = { center: getDefaultCenterFilter() };
    setFilters(defaultFilters);
    setDateRange(null);
    setModalityAllSelected(true);
    setSelectedPriority(null);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    filterResults(defaultFilters, 1, pagination.pageSize);
  }

  const handleFilterSelection = selectedSavedFilter => {
    const filterString = selectedSavedFilter.uf_filter_json;
    const filterJson = JSON.parse(filterString);

    setFilters(filterJson);
  };

  const handleQuickFilters = selectedFilter => {
    // Compound shortcut: apply the whole filter set + date range at once.
    if (selectedFilter.filterRaw) {
      const { filters: f, dateRange: dr } = shortcutToFilterAndRange(selectedFilter.filterRaw);
      setFilters(f);
      setModalityAllSelected(!(f.modality && f.modality.length));
      if (dr) setDateRange(dr);
      return;
    }

    // LEGACY (pre-compound shortcuts) — kept for context. Quick filters used to
    // be single-key rows: qf_type DATE set the date range, TOGGLE flipped a
    // boolean flag (Emergency/Re-reported), NA set one field=value. Migration
    // 20260618000003 converted all rows to compound qf_filter_json, so every
    // shortcut now takes the `filterRaw` path above and this branch is unused.
    //
    // const { qf_type, qf_date_start, qf_date_end, qf_key, qf_value } = selectedFilter;
    // if (qf_type === 'DATE') {
    //   const toDate = dayjs().subtract(qf_date_end, 'days').hour(23).minute(59).second(0);
    //   const fromDate = dayjs().subtract(qf_date_start, 'days').hour(0).minute(0).second(0);
    //   setDateRange([fromDate, toDate]);
    // } else if (qf_type === 'TOGGLE') {
    //   handleFilterChange(qf_key, !filters[qf_key]);
    // } else {
    //   setFilters({
    //     ...filters,
    //     [qf_key]: qf_key === 'modality' && qf_value && !Array.isArray(qf_value) ? [qf_value] : qf_value,
    //   });
    // }
  };

  // Action handlers using shared utilities
  const openViewer = (record) => actionHandlers.openViewer(record, userDetails);
  const openRadDesk = (record) => actionHandlers.openRadDesk(record, userDetails);
  const openReport = (record) => {
    // Keep click behavior in sync with PACS list: in dual viewer mode,
    // patient-name/report clicks should open the viewer instead of report editor.
    if ((window as any)?.ENV_CONFIG?.DUAL_VIEWER_MODE) {
      return openViewer(record);
    }

    return actionHandlers.openReport(record, userDetails);
  };
  const printReport = (rec) => actionHandlers.printReport(rec, setPrintLoading);
  const downloadDocxReport = (rec) => actionHandlers.downloadDocxReport(rec, setPrintLoading);
  const viewReport = (rec) => actionHandlers.viewReport(rec, setPrintLoading, setViewerModal);
  // Status-column click opens the report in Chrome's native PDF viewer (a
  // separate popup window) rather than the in-app modal.
  const openReportInWindow = (rec) => actionHandlers.openReportInWindow(rec, setPrintLoading);


  const showOrdDetails = (rec) => {
    setOrderInfoModal({ visible: true, data: rec });
  }

  const closeOrdInfo = () => {
    setOrderInfoModal({ visible: false, data: null });
  }

  const handleShareStudy = (record) => {
    setSelectedStudyForShare(record);
    setShareModalVisible(true);
  };

  const handleDownloadStudy = (record) => {
    setSelectedStudyForDownload(record);
    setDownloadModalVisible(true);
  };

  const handleSplitStudy = (record) => {
    if (!record?.ps_study_uid) {
      message.error('Study UID not found');
      return;
    }
    setSelectedStudyForSplit(record);
    setSplitStudyModalVisible(true);
  };

  const handleExportStudy = (record) => {
    if (!record?.ps_study_uid) {
      message.error('Study UID not found');
      return;
    }
    setSelectedStudyForExport(record);
    setExportModalVisible(true);
  };

  // Handle series selection button click
  const handleSelectSeries = (record) => {
    if (!record?.ps_study_uid) {
      message.error('Study UID not found');
      return;
    }

    setSeriesSelectionModal({
      open: true,
      studyUID: record.ps_study_uid,
      record,
    });
  };

  const closeSeriesModal = () =>
    setSeriesSelectionModal(prev => ({ ...prev, open: false }));

  // Open viewer with the selected series in a new window. Routes through
  // MonitorManager so the study loads from its own centre's cache proxy.
  const handleOpenSeries = (studyUID, selectedSeries, record) => {
    openSeriesViewer(studyUID, selectedSeries, record);
  };

  const editSignedOffReport = (rec) => {
    openReportingWindow(`/oo-report?order_ids=${rec.pacs_order.pacs_ord_id}`);
  };

  // --- Column configuration (shared with PACS page, module 'pacs_list') ---

  const getBaseOrderColumns = useCallback(() => {
    // Only the patient-name click handler shapes the data columns now; the action
    // icons live in the leading cluster (buildLeadingActionColumns), not here.
    return orderColumns({
      openReportEditor: openReport,
    });
  }, []);

  const saveColumnPreferences = async (newColumnOrder: any[], newPinnedColumns?: PinnedColumnsConfig) => {
    try {
      const columnKeys = newColumnOrder.map((col: any) => {
        const colId = col.key || col.dataIndex || '';
        const identifier: any = {
          key: col.key || null,
          dataIndex: col.dataIndex || null,
          title: typeof col.title === 'string' ? col.title : null,
        };
        if (typeof col.title !== 'string' && col.title?.props?.children) {
          if (typeof col.title.props.children === 'string') {
            identifier.title = col.title.props.children;
          } else if (Array.isArray(col.title.props.children)) {
            identifier.title = col.title.props.children
              .filter((child: any) => typeof child === 'string')
              .join('');
          }
        }
        if (newPinnedColumns) {
          if (newPinnedColumns.left.includes(colId)) {
            identifier.pinned = 'left';
          } else if (newPinnedColumns.right.includes(colId)) {
            identifier.pinned = 'right';
          }
        }
        return identifier;
      });

      await makePostCall('/save-column-preferences', {
        user_id: getUserDetails()?.username,
        module: 'pacs_list',
        column_order: JSON.stringify(columnKeys),
      });
    } catch (error) {
      console.error('Failed to save worklist column preferences:', error);
    }
  };

  const loadColumnPreferences = async () => {
    try {
      const response = await makeGetCall(
        `/get-column-preferences?user_id=${getUserDetails()?.username}&module=pacs-list`
      );

      const baseColumns = getBaseOrderColumns();

      const savedColumnKeys = response.data?.data?.ucp_column_order
        ? JSON.parse(response.data.data.ucp_column_order)
        : null;

      // Discard saved preferences that predate the reference flex layout.
      const isStalePref =
        Array.isArray(savedColumnKeys) &&
        !savedColumnKeys.some((c: any) => (c.key || c.dataIndex) === REFERENCE_LAYOUT_MARKER);

      if (savedColumnKeys && !isStalePref) {
        const loadedPinned: PinnedColumnsConfig = { left: [], right: [] };
        savedColumnKeys.forEach((savedCol: any) => {
          const colId = savedCol.key || savedCol.dataIndex;
          if (colId && savedCol.pinned === 'left') {
            loadedPinned.left.push(colId);
          } else if (colId && savedCol.pinned === 'right') {
            loadedPinned.right.push(colId);
          }
        });
        setPinnedColumns(loadedPinned);

        const savedVisibleIds = savedColumnKeys.map((col: any) => col.key || col.dataIndex).filter(Boolean);
        setVisibleColumnIds(savedVisibleIds);

        const reorderedColumns: any[] = [];
        savedVisibleIds.forEach((colId: any) => {
          const col = baseColumns.find((c: any) => (c.key || c.dataIndex) === colId);
          if (col) {
            reorderedColumns.push(col);
          }
        });

        setColumnOrder(reorderedColumns);
      } else {
        // No saved prefs (or stale pre-reference-layout prefs) - default order,
        // nothing pinned right (actions are a leading cluster now).
        setPinnedColumns({ left: [], right: [] });
        setColumnOrder([]);
      }
    } catch (error) {
      console.error('Failed to load worklist column preferences:', error);
      setPinnedColumns({ left: [], right: [] });
      setColumnOrder([]);
    }
  };

  // All column configs for the ColumnConfigurator modal
  const allColumnConfigs: ColumnConfig[] = useMemo(() => {
    const baseColumns = getBaseOrderColumns();
    return baseColumns.map((col: any) => {
      let headerName = '';
      if (typeof col.title === 'string') {
        headerName = col.title;
      } else if (col.title?.props?.children) {
        const children = col.title.props.children;
        if (typeof children === 'string') {
          headerName = children;
        } else if (Array.isArray(children)) {
          headerName = children.filter((c: any) => typeof c === 'string').join('');
        }
      }
      return {
        colId: col.key || col.dataIndex || '',
        headerName: headerName || col.key || col.dataIndex || '',
      };
    });
  }, []);

  const handleColumnConfigSave = useCallback((newVisibleColumnIds: string[], newPinnedColumns: PinnedColumnsConfig) => {
    setVisibleColumnIds(newVisibleColumnIds);
    setPinnedColumns(newPinnedColumns);

    const baseColumns = getBaseOrderColumns();
    const reorderedColumns: any[] = [];
    newVisibleColumnIds.forEach((colId: string) => {
      const col = baseColumns.find((c: any) => (c.key || c.dataIndex) === colId);
      if (col) {
        reorderedColumns.push(col);
      }
    });

    setColumnOrder(reorderedColumns);
    saveColumnPreferences(reorderedColumns, newPinnedColumns);
  }, []);

  // Compute the final columns to pass to AgGridTable, respecting visibility, order and pinning
  const configuredColumns = useMemo(() => {
    const baseColumns = columnOrder.length > 0
      ? columnOrder
      : getBaseOrderColumns();

    return baseColumns.map((col: any) => {
      const colId = col.key || col.dataIndex;
      let fixed: string | undefined;
      if (pinnedColumns.left.includes(colId)) {
        fixed = 'left';
      } else if (pinnedColumns.right.includes(colId)) {
        fixed = 'right';
      }
      return { ...col, fixed };
    });
  }, [columnOrder, pinnedColumns]);

  // Gear icon for the column configurator. The Actions column is now a leading
  // (left-pinned) cluster outside the configurable column system, so the gear
  // lives on the Status ("Details") header, present for every role.
  const columnHeaderActions: ColumnHeaderActions = useMemo(() => {
    return {
      status: {
        icon: <SettingOutlined />,
        onClick: () => setColumnConfiguratorVisible(true),
        tooltip: 'Configure Columns',
      },
    };
  }, []);

  // Leading (left-pinned) action columns - the full action cluster (or the
  // technician three-icon set), mirroring the reference RIS layout.
  const leadingColumns = useMemo(
    () =>
      buildLeadingActionColumns({
        openViewer,
        showOrdDetails,
        openRadDesk,
        openReportEditor: openReport,
        viewNotes,
        printReport,
        downloadDocxReport,
        viewReport,
        editSignedOffReport,
        handleShareStudy,
        handleDownloadStudy,
        handleExportStudy,
        handleSelectSeries,
        splitStudy: handleSplitStudy,
        role: userDetails?.user_type,
        pageType: 'pacs',
      }),
    []
  );

  const openSelectedStudies = async () => {
    if (!hasReportingPermission(userDetails)) {
      message.info('You do not have the permission to do reporting');
      return;
    }
    // Drops "Not for reporting" studies, applies the unconfirmed rule (block or warn)
    // and handles all the skipped/abort messaging.
    const reportable = await actionHandlers.filterReportableStudies(selectedStudyRecords);
    if (!reportable) return;
    // Checked on what would actually open, not the raw selection: studies dropped
    // just above (not for reporting / cancelled / unconfirmed) must not count
    // against the user's cap.
    if (!actionHandlers.withinReportLimit(reportable.length, userDetails)) return;
    const orderIds = reportable.map(rec => rec?.pacs_order?.pacs_ord_id).join(',');
    await openReportingWindow(`/oo-report?order_ids=${orderIds}`);
    // Clear the selection once the studies have been opened so the user
    // doesn't have to uncheck each row manually.
    setSelectedRowKeys([]);
  };

  return (
    <Spin spinning={printLoading}>
    <div>
      <div style={{ padding: '0.75rem 1.25rem 0' }}>
        <div style={{
          background: '#FFFFFF',
          borderRadius: '0.75rem',
          border: '1px solid #E2E8F1',
          padding: '1rem 1.25rem',
          marginBottom: '1rem'
        }}>
          {/* Quick filters + action buttons share one row to save vertical space */}
          <div className="filter-toolbar">
            <div className="filter-toolbar-main">
              <SavedSearches
                savedFilters={savedFilters || []}
                handleFilterSelection={handleFilterSelection}
                quickFilters={quickFilters || []}
                handleQuickFilters={handleQuickFilters}
                currentFilters={filters}
              />
            </div>
            <div className="filter-toolbar-actions">
              {hasReportingPermission(userDetails) && (
                <button
                  className="btn-primary btn-compact"
                  disabled={selectedRowKeys.length === 0}
                  onClick={openSelectedStudies}
                >
                  Open Studies{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
                </button>
              )}
              <button
                className="btn-primary btn-compact"
                onClick={() => filterResults(filters)}
              >
                <SearchOutlined />
                Search Worklist
              </button>
              <button
                className="btn-secondary btn-compact"
                onClick={() => { clearFilters() }}
              >
                Clear Search
              </button>
            </div>
          </div>

          <div className="filter-layout">
            <div className="wl-filters-row filter-layout-inputs">
              <div className="wl-field wl-field--name">
                <FyzksInput
                  size="small"
                  placeholder="Patient Name"
                  value={filters['pat_name'] || ''}
                  onChange={(e) => handleFilterChange('pat_name', e.target.value)}
                />
              </div>

              {['radiologist', 'hod'].includes(userDetails?.user_type) && (
                <div className="wl-field wl-field--id">
                  <FyzksInput
                    size="small"
                    placeholder={getPatientIdLabel()}
                    value={filters['po_pin'] || ''}
                    onChange={e => handleFilterChange('po_pin', e.target.value)}
                  />
                </div>
              )}

              {['radiologist', 'hod'].includes(userDetails?.user_type) && (
                <div className="wl-field wl-field--acc">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.ACCESSION_NUMBER_SHORTEST}
                    value={filters['po_acc_no'] || ''}
                    onChange={e => handleFilterChange('po_acc_no', e.target.value)}
                  />
                </div>
              )}

              {['radiologist', 'hod'].includes(userDetails?.user_type) && (
                <div className="wl-field wl-field--visitid">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.VISIT_ID}
                    value={filters['po_visit_number'] || ''}
                    onChange={e => handleFilterChange('po_visit_number', e.target.value)}
                  />
                </div>
              )}

              <div className="wl-field wl-field--ordno">
                <FyzksInput
                  size="small"
                  placeholder="Order No"
                  value={filters['po_ord_no'] || ''}
                  onChange={(e) => handleFilterChange('po_ord_no', e.target.value)}
                />
              </div>

              <div className="wl-field wl-field--refdoc">
                <FyzksInput
                  size="small"
                  allowClear
                  placeholder="Ref Doc."
                  value={filters['po_ref_doc'] || ''}
                  onChange={(e) => handleFilterChange('po_ref_doc', e.target.value)}
                />
              </div>

              <div className="wl-field wl-field--body">
                <FyzksInput
                  size="small"
                  placeholder="Body Part / Study Desc"
                  value={filters['po_body_part'] || ''}
                  onChange={(e) => handleFilterChange('po_body_part', e.target.value)}
                />
              </div>

              <div className="wl-field wl-field--status">
                <Select
                  size="small"
                  mode="multiple"
                  allowClear
                  maxTagCount={1}
                  className="single-line-tags"
                  maxTagPlaceholder={maxTagTooltip}
                  placeholder="Status"
                  value={filters['po_status']}
                  style={{ width: '100%' }}
                  options={statusOptions}
                  onChange={(val) => handleFilterChange('po_status', val)}
                />
              </div>

              <div className="wl-field wl-field--site">
                <Select
                  size="small"
                  allowClear
                  placeholder="Site"
                  value={filters['po_site']}
                  style={{ width: '100%' }}
                  options={centers}
                  onChange={(val) => handleFilterChange('po_site', val)}
                />
              </div>

              <div className="wl-field wl-field--center">
                <Select
                  size="small"
                  mode="multiple"
                  allowClear
                  maxTagCount={1}
                  className="single-line-tags"
                  maxTagPlaceholder={maxTagTooltip}
                  placeholder="Center/Group"
                  value={filters['center']}
                  style={{ width: '100%' }}
                  options={centers}
                  onChange={(val) => handleFilterChange('center', val)}
                  popupMatchSelectWidth={false}
                  dropdownStyle={{ minWidth: '14rem' }}
                />
              </div>

              {['radiologist', 'hod'].includes(userDetails?.user_type) && (
                <div className="wl-field wl-field--location">
                  <Select
                    size="small"
                    allowClear
                    placeholder="Location"
                    value={filters['modality_location']}
                    style={{ width: '100%' }}
                    options={locationOptions}
                    onChange={val => handleFilterChange('modality_location', val)}
                  />
                </div>
              )}

              {['radiologist', 'hod'].includes(userDetails?.user_type) && (
                <div className="wl-field wl-field--regtype">
                  <Select
                    size="small"
                    allowClear
                    placeholder="Registration Type"
                    value={filters['po_pat_class']}
                    style={{ width: '100%' }}
                    options={regTypeOptions}
                    onChange={val => handleFilterChange('po_pat_class', val)}
                  />
                </div>
              )}

              {Boolean(CENTER_RADIOLOGY_URL) && (
                <div className="wl-field wl-field--hisstatus">
                  <Select
                    size="small"
                    mode="multiple"
                    allowClear
                    maxTagCount={1}
                    className="single-line-tags"
                    maxTagPlaceholder={maxTagTooltip}
                    placeholder="HIS Status"
                    value={filters['po_his_status']}
                    style={{ width: '100%' }}
                    options={hisStatusOptions}
                    onChange={(val) => handleFilterChange('po_his_status', val)}
                  />
                </div>
              )}

              {['radiologist', 'hod'].includes(userDetails?.user_type) && (
                <div className="wl-field wl-field--repby">
                  <Select
                    size="small"
                    allowClear
                    showSearch
                    maxTagCount={1}
                    className="single-line-tags"
                    maxTagPlaceholder={maxTagTooltip}
                    mode="multiple"
                    autoClearSearchValue={false}
                    popupMatchSelectWidth={false}
                    placeholder="Reported By"
                    value={filters['po_reported_by']}
                    style={{ width: '100%' }}
                    options={radHodOptions}
                    optionRender={(option) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Checkbox checked={filters['po_reported_by']?.includes(option.value)} style={{ pointerEvents: 'none' }} />
                        <span>{option.label}</span>
                      </div>
                    )}
                    onChange={val => handleFilterChange('po_reported_by', val)}
                  />
                </div>
              )}

              <div className="wl-field wl-field--date">
                <RangePicker
                  size="small"
                  placeholder={['Study Date', '']}
                  showTime={{
                    format: 'HH:mm',
                    use12Hours: false,
                    defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                  }}
                  format="YYYY-MM-DD HH:mm"
                  value={dateRange}
                  onChange={(val) => {
                    if (val) {
                      setDateRange([val[0], val[1]]);
                    } else {
                      setDateRange(null);
                    }
                  }}
                />
              </div>
              <div className="wl-field wl-field--presets">
                <div className="date-with-presets">
                  <QuickDatePresets value={dateRange} onChange={setDateRange} />
                </div>
              </div>
            </div>

            <div className="filter-layout-modalities">
              <div className="filter-modalities-header">
                <Checkbox
                  className="filter-modalities-selectall"
                  checked={modalityAllSelected}
                  indeterminate={!modalityAllSelected && (filters['modality']?.length > 0)}
                  onChange={e => {
                    setModalityAllSelected(e.target.checked);
                    handleFilterChange('modality', []);
                  }}
                >
                  Select all
                </Checkbox>
              </div>
              <Checkbox.Group
                value={modalityAllSelected ? sortedModalityOptions.map(o => o.value) : (filters['modality'] || [])}
                onChange={(vals) => {
                  const allChecked = vals.length === sortedModalityOptions.length;
                  setModalityAllSelected(allChecked);
                  handleFilterChange('modality', allChecked ? [] : vals);
                }}
              >
                {sortedModalityOptions.map(opt => (
                  <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
                ))}
              </Checkbox.Group>
            </div>
          </div>
      </div>
      <div className='orders-list'>
        <PacsAgGrid
          height="calc(100vh - 18.5rem)"
          loading={orders.loading}
          rowData={orders.data || []}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={(keys) => setSelectedRowKeys(keys)}
          onOpenReport={openReport}
          onViewReport={openReportInWindow}
          columns={configuredColumns}
          leadingColumns={leadingColumns}
          columnHeaderActions={columnHeaderActions}
          handleSelectSeries={handleSelectSeries}
        />
        {/* External pagination for server-side paging */}
        <div className="pacs-ag-grid-pagination">
          <Pagination
            size="small"
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={orders?.total || 0}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
            onChange={(page, pageSize) => {
              setPagination({ current: page, pageSize });
              setSelectedRowKeys([]);
              filterResults(filters, page, pageSize);
            }}
            showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`}
          />
        </div>

        {/* Column Configurator Modal */}
        <ColumnConfigurator
          visible={columnConfiguratorVisible}
          onClose={() => setColumnConfiguratorVisible(false)}
          allColumns={allColumnConfigs}
          visibleColumnIds={visibleColumnIds}
          pinnedColumns={pinnedColumns}
          onSave={handleColumnConfigSave}
        />

        {
          viewNotesModal && viewNotesModal.visible && (
            <Modal
              open={viewNotesModal.visible}
              onCancel={() => {
                if (noteEditActive) {
                  setNoteEditActive(false);
                  return;
                }

                setViewNotesModal(null);
                setNoteEditActive(false);
              }}
              onOk={async () => {
                if (saveNoteHandler) {
                  const saved = await saveNoteHandler();
                  if (!saved) {
                    return;
                  }
                }
              }}
              style={{ width: '100%', height: '100%' }}
              width={'90%'}
              okButtonProps={{
                style: {
                  marginTop: '20px',
                },
                disabled: !noteEditActive,
              }}
              okText="Save"
              cancelText={noteEditActive ? 'Cancel' : 'Close'}
            >
              <ViewNotes
                handleNoteSelection={handleNoteSelection}
                selectedNote={selectedNote}
                viewNotesModal={viewNotesModal}
                isEditMode={noteEditActive}
                onEditNoteStateChange={setNoteEditActive}
                onSaveNoteRegister={setSaveNoteHandler}
              />
            </Modal>
          )
        }



        {/* Order Info Modal */}
        {
          orderInfoModal && orderInfoModal.visible && (
            <Modal
              open={orderInfoModal.visible}
              onCancel={closeOrdInfo}
              footer={[
                <Button key="cancel" onClick={closeOrdInfo}>
                  Cancel
                </Button>
              ]}
              width={700}
            >
              <OrderInfoPopup
                data={orderInfoModal.data}
                closeModal={closeOrdInfo}
                refreshTable={() => filterResults(filters)}
              />
            </Modal>
          )
        }

        {/* PDF Viewer Modal */}
        {
          viewerModal && (
            <Modal
              width={1000}
              open={viewerModal?.visible}
              onCancel={() => { setViewerModal({ visible: false }) }}
              okButtonProps={{ style: { display: 'none' } }}
              style={{ top: 20 }}
              styles={{
                body: {
                  height: '85vh',
                  maxHeight: '85vh',
                  overflow: 'hidden',
                  padding: 0
                }
              }}
            >
              <div style={{ height: '100%', width: '100%', overflow: 'auto' }}>
                <PdfViewer
                  order_id={viewerModal?.order_id}
                  patient_id={viewerModal?.patient_id}
                  pdfArrayBuffer={viewerModal?.data}
                  patientData={viewerModal?.patientData}
                />
              </div>
            </Modal>
          )
        }

        {/* Share Study Modal */}
        {selectedStudyForShare && (
          <ShareStudyModal
            visible={shareModalVisible}
            onCancel={() => {
              setShareModalVisible(false);
              setSelectedStudyForShare(null);
            }}
            studyUID={selectedStudyForShare?.ps_study_uid}
            studyDescription={selectedStudyForShare?.pacs_order?.po_diag_desc}
            patientName={selectedStudyForShare?.pacs_order?.patient?.pat_name}
            patientGender={selectedStudyForShare?.pacs_order?.patient?.pat_sex}
            patientDOB={selectedStudyForShare?.pacs_order?.patient?.pat_dob}
            studyDate={selectedStudyForShare?.ps_study_dt_tm}
          />
        )}

        {/* Download Study Modal */}
        {selectedStudyForDownload && (
          <DownloadStudyModal
            visible={downloadModalVisible}
            onCancel={() => {
              setDownloadModalVisible(false);
              setSelectedStudyForDownload(null);
            }}
            studyUID={selectedStudyForDownload?.ps_study_uid}
            studyDescription={selectedStudyForDownload?.pacs_order?.po_diag_desc}
            patientName={selectedStudyForDownload?.pacs_order?.patient?.pat_name}
            accessionNumber={selectedStudyForDownload?.pacs_order?.po_acc_no}
            studyData={selectedStudyForDownload}
          />
        )}

        {/* Split Study Modal */}
        {selectedStudyForSplit && (
          <SplitStudyModal
            visible={splitStudyModalVisible}
            record={selectedStudyForSplit}
            onCancel={() => {
              setSplitStudyModalVisible(false);
              setSelectedStudyForSplit(null);
            }}
            onSuccess={() => {
              // Refresh so the newly-created split order appears in the list.
              filterResults(filters);
            }}
          />
        )}

        {/* Export Study Modal */}
        {selectedStudyForExport && (
          <ExportStudyModal
            visible={exportModalVisible}
            studyUID={selectedStudyForExport?.ps_study_uid}
            studyModality={selectedStudyForExport?.pacs_order?.po_modality}
            onCancel={() => {
              setExportModalVisible(false);
              setSelectedStudyForExport(null);
            }}
            onSuccess={() => {
              filterResults(filters);
              // Clear the selection after a successful push so the user
              // doesn't have to uncheck the row manually.
              setSelectedRowKeys([]);
            }}
          />
        )}

        {/* Series Selection modal (shared with PACS) */}
        <SeriesSelectionModal
          open={seriesSelectionModal.open}
          studyUID={seriesSelectionModal.studyUID}
          record={seriesSelectionModal.record}
          onClose={closeSeriesModal}
          onOpenSeries={handleOpenSeries}
        />

      </div>
      </div>
    </div>
    </Spin>
  );
}

export default MyWorklist;
